import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Code as CodeIcon,
  Brush as BrushIcon,
  Storage as StorageIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import DevSecOpsTabs from '../../components/devsecops/DevSecOpsTabs';
import { useNavigate } from 'react-router-dom';
import DevSecOpsComponentCard, { DevSecOpsComponentDto } from '../../components/devsecops/DevSecOpsComponentCard';
import api, { ApiResponse, authService, User } from '../../services/api';
import { devsecopsApi } from '../../services/devsecopsApi';

const DevSecOpsTeams: React.FC = () => {
  const navigate = useNavigate();
  const [components, setComponents] = useState<DevSecOpsComponentDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUserRole, setCurrentUserRole] = useState<User['role'] | undefined>(undefined);

  const handleBack = () => {
    navigate('/devsecops');
  };

  const devSecOpsTeam = [
    { role: 'DevSecOps Lead', count: 1, responsibilities: 'Overall technical leadership, architecture decisions, CI/CD strategy' },
    { role: 'Senior DevOps Engineer', count: 2, responsibilities: 'Pipeline development, infrastructure automation, deployment orchestration' },
    { role: 'Security Engineer', count: 1, responsibilities: 'Security scanning, vulnerability management, compliance validation' },
    { role: 'Cloud Engineer', count: 2, responsibilities: 'Cloud infrastructure, containerization, Kubernetes management' },
    { role: 'Automation Engineer', count: 2, responsibilities: 'Test automation, policy-as-code, configuration management' }
  ];

  const uxdTeam = [
    { role: 'UX/UI Lead', count: 1, responsibilities: 'Design strategy, user research, accessibility compliance' },
    { role: 'Senior UX Designer', count: 2, responsibilities: 'Wireframes, prototypes, user flows, design systems' },
    { role: 'UI Developer', count: 2, responsibilities: 'React components, responsive design, Section 508 implementation' },
    { role: 'UX Researcher', count: 1, responsibilities: 'User testing, usability studies, feedback analysis' }
  ];

  const infraTeam = [
    { role: 'Infrastructure Lead', count: 1, responsibilities: 'Infrastructure strategy, capacity planning, disaster recovery' },
    { role: 'Site Reliability Engineer', count: 2, responsibilities: 'Monitoring, alerting, incident response, performance tuning' },
    { role: 'Database Administrator', count: 1, responsibilities: 'Database optimization, backup/recovery, data migration' },
    { role: 'Network Engineer', count: 1, responsibilities: 'Network architecture, security, connectivity, load balancing' }
  ];

  const keyPersonnelQualifications = [
    'Minimum 5 years experience in federal government IT projects',
    'Active security clearance or ability to obtain clearance',
    'Demonstrated expertise in DevSecOps, CI/CD, and cloud technologies',
    'Strong understanding of federal compliance requirements (FISMA, Section 508)',
    'Excellent communication and collaboration skills',
    'Experience with Agile/Scrum methodologies'
  ];

  const teamCapabilities = [
    {
      icon: <CodeIcon />,
      title: 'DevSecOps Excellence',
      description: 'Automated CI/CD pipelines, infrastructure as code, policy-as-code enforcement, and shift-left security practices'
    },
    {
      icon: <BrushIcon />,
      title: 'User-Centered Design',
      description: 'Section 508 compliant interfaces, responsive design, user research, and iterative design improvements'
    },
    {
      icon: <StorageIcon />,
      title: 'Infrastructure Expertise',
      description: 'Cloud architecture, containerization, monitoring/alerting, and 24/7 operational support'
    }
  ];

  useEffect(() => {
    const fetchComponents = async () => {
      try {
        setLoading(true);
        const response = await devsecopsApi.get<ApiResponse<DevSecOpsComponentDto[]>>(
          '/devsecops/components',
          {
            params: { page: 'teams' }
          }
        );

        const data = response.data.data || [];
        setComponents(data);
        setError(null);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unable to load teams components';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchComponents();
  }, []);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const user = await authService.getCurrentUser();
        setCurrentUserRole(user.role);
      } catch (_err) {
        // unauthenticated users will see read-only cards
      }
    };

    loadUser();
  }, []);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to DevSecOps Program"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: '#003366', fontWeight: 'bold' }}>
            Team Composition
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            DevSecOps, UXD/IxD, and Infrastructure Team Structure
          </Typography>
        </Box>
      </Box>

      {/* DevSecOps Tabs */}
      <DevSecOpsTabs activeTab="teams" />

      {/* Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Team Overview
          </Typography>
          <Typography variant="body1" paragraph>
            Our ODOS IV team structure is designed to deliver comprehensive DevSecOps, UX, and infrastructure services
            with a focus on automation, security, and user experience. Teams are organized into three core groups,
            each led by experienced key personnel with deep expertise in federal government IT modernization.
          </Typography>
        </CardContent>
      </Card>

      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mb: 3 }}>
        Team Capabilities & Roles
      </Typography>

      {loading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Loading DevSecOps team components...
        </Typography>
      )}

      {error && (
        <Typography variant="body2" color="error" role="alert" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {components.map((component: DevSecOpsComponentDto) => (
          <Grid item xs={12} md={6} key={component.key}>
            <DevSecOpsComponentCard
              component={component}
              currentUserRole={currentUserRole}
              onUpdated={(updated) => {
                setComponents((prev: DevSecOpsComponentDto[]) =>
                  prev.map((c: DevSecOpsComponentDto) => (c.key === updated.key ? updated : c))
                );
              }}
            />
          </Grid>
        ))}
      </Grid>

      {/* Team Capabilities */}
      {/* Static team capabilities visuals remain below the dynamic cards */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mb: 3 }}>
        Core Team Capabilities
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {teamCapabilities.map((capability, index) => (
          <Grid item xs={12} md={4} key={index}>
            <Card sx={{ height: '100%', border: '2px solid #003366' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ color: '#003366', mr: 2 }}>
                    {capability.icon}
                  </Box>
                  <Typography variant="h6" fontWeight="bold">
                    {capability.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {capability.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* DevSecOps Team */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <CodeIcon sx={{ color: 'primary.main', mr: 2, fontSize: 32 }} />
            <Typography variant="h5" color="primary" fontWeight="bold">
              DevSecOps Team
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Role</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Count</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Key Responsibilities</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {devSecOpsTeam.map((member, index) => (
                  <TableRow key={index} hover>
                    <TableCell sx={{ fontWeight: 'medium' }}>{member.role}</TableCell>
                    <TableCell>
                      <Chip label={member.count} size="small" color="primary" />
                    </TableCell>
                    <TableCell>{member.responsibilities}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* UXD/IxD Team */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <BrushIcon sx={{ color: 'primary.main', mr: 2, fontSize: 32 }} />
            <Typography variant="h5" color="primary" fontWeight="bold">
              UXD / IxD Team
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Role</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Count</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Key Responsibilities</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {uxdTeam.map((member, index) => (
                  <TableRow key={index} hover>
                    <TableCell sx={{ fontWeight: 'medium' }}>{member.role}</TableCell>
                    <TableCell>
                      <Chip label={member.count} size="small" color="primary" />
                    </TableCell>
                    <TableCell>{member.responsibilities}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Infrastructure Team */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <StorageIcon sx={{ color: 'primary.main', mr: 2, fontSize: 32 }} />
            <Typography variant="h5" color="primary" fontWeight="bold">
              Infrastructure & Integration Team
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Role</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Count</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Key Responsibilities</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {infraTeam.map((member, index) => (
                  <TableRow key={index} hover>
                    <TableCell sx={{ fontWeight: 'medium' }}>{member.role}</TableCell>
                    <TableCell>
                      <Chip label={member.count} size="small" color="primary" />
                    </TableCell>
                    <TableCell>{member.responsibilities}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Key Personnel Qualifications */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <PersonIcon sx={{ color: 'primary.main', mr: 2, fontSize: 32 }} />
            <Typography variant="h5" color="primary" fontWeight="bold">
              Key Personnel Qualifications
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            All key personnel meet the following minimum qualifications:
          </Typography>
          <List>
            {keyPersonnelQualifications.map((qual, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <CheckCircleIcon color="success" />
                </ListItemIcon>
                <ListItemText primary={qual} />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    </Container>
  );
};

export default DevSecOpsTeams;
